/**
 * Sistema de Detecção de Browsers e WebViews
 * Detecta Instagram, Facebook, TikTok e outros browsers in-app
 */

export type BrowserType = 'instagram' | 'facebook' | 'tiktok' | 'whatsapp' | 'standard';

export interface BrowserInfo {
  type: BrowserType;
  isWebView: boolean;
  isInstagram: boolean;
  isFacebook: boolean;
  isTikTok: boolean;
  isWhatsApp: boolean;
  isMobile: boolean;
  userAgent: string;
}

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

/**
 * Detecta o tipo de browser baseado no User-Agent
 */
export const detectBrowserType = (): BrowserType => {
  if (typeof window === 'undefined') return 'standard';
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Instagram WebView
  if (userAgent.includes('instagram')) {
    return 'instagram';
  }
  
  // Facebook WebView (FBAN = Facebook App, FBAV = Facebook App Version)
  if (userAgent.includes('fban') || userAgent.includes('fbav') || 
      userAgent.includes('facebook')) {
    return 'facebook';
  }
  
  // TikTok WebView
  if (userAgent.includes('tiktok') || userAgent.includes('musical_ly')) {
    return 'tiktok';
  }
  
  // WhatsApp WebView
  if (userAgent.includes('whatsapp')) {
    return 'whatsapp';
  }
  
  return 'standard';
};

/**
 * Detecta se está em um WebView (in-app browser)
 */
export const isWebView = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Indicadores comuns de WebView
  const webViewIndicators = [
    'instagram',
    'fban',
    'fbav',
    'tiktok',
    'whatsapp',
    'wv', // WebView indicator
    'webview'
  ];
  
  // Verifica se não tem window.ancestorOrigins (comum em WebViews)
  const hasNoAncestorOrigins = !window.location.ancestorOrigins;
  
  // Verifica indicadores no User-Agent
  const hasWebViewIndicator = webViewIndicators.some(indicator => 
    userAgent.includes(indicator)
  );
  
  return hasWebViewIndicator || (hasNoAncestorOrigins && isMobile());
};

/**
 * Detecta se é um dispositivo móvel
 */
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileIndicators = [
    'android',
    'iphone',
    'ipad',
    'ipod',
    'mobile',
    'phone',
    'tablet'
  ];
  
  return mobileIndicators.some(indicator => userAgent.includes(indicator));
};

/**
 * Obtem informações completas do browser
 */
export const getBrowserInfo = (): BrowserInfo => {
  const type = detectBrowserType();
  const userAgent = typeof window !== 'undefined' ? navigator.userAgent : '';
  
  return {
    type,
    isWebView: isWebView(),
    isInstagram: type === 'instagram',
    isFacebook: type === 'facebook',
    isTikTok: type === 'tiktok',
    isWhatsApp: type === 'whatsapp',
    isMobile: isMobile(),
    userAgent
  };
};

/**
 * Hook para usar informações do browser
 */
export const useBrowserDetection = () => {
  return getBrowserInfo();
};

/**
 * Detecta se deve usar otimizações específicas para WebView
 */
export const shouldUseWebViewOptimizations = (): boolean => {
  const browserInfo = getBrowserInfo();
  return browserInfo.isWebView;
};

/**
 * Detecta se deve usar otimizações específicas para Instagram
 */
export const shouldUseInstagramOptimizations = (): boolean => {
  return detectBrowserType() === 'instagram';
};

/**
 * Debug: Retorna informações de debug do browser
 */
export const getDebugInfo = () => {
  if (typeof window === 'undefined') {
    return { error: 'Window not available (SSR)' };
  }
  
  const browserInfo = getBrowserInfo();
  
  return {
    ...browserInfo,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      ratio: window.devicePixelRatio || 1
    },
    support: {
      webp: canUseWebP(),
      avif: canUseAVIF(),
      intersectionObserver: 'IntersectionObserver' in window,
      serviceWorker: 'serviceWorker' in navigator
    },
    network: {
      connection: (navigator as unknown as Record<string, { effectiveType?: string }>).connection?.effectiveType || 'unknown',
      downlink: (navigator as unknown as Record<string, { downlink?: number }>).connection?.downlink || 'unknown'
    }
  };
};

/**
 * Detecta suporte a WebP
 */
export const canUseWebP = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

/**
 * Detecta suporte a AVIF
 */
export const canUseAVIF = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  try {
    return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
  } catch {
    return false;
  }
};

// Aliases para compatibilidade com código existente
export const detectInstagramBrowser = shouldUseInstagramOptimizations;
export const getInstagramOptimizations = () => ({
  isInstagram: shouldUseInstagramOptimizations(),
  shouldOptimizeImages: shouldUseWebViewOptimizations(),
  shouldSimplifyAnimations: shouldUseWebViewOptimizations(),
  shouldEnhanceAnalytics: shouldUseWebViewOptimizations(),
  shouldShowEscapeButton: shouldUseInstagramOptimizations()
});
